<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { RouterLink } from 'vue-router'
import { usePageSeo } from '@/composables/usePageSeo'

usePageSeo({
  title: 'Terms of Use',
  description: 'The terms that govern your use of Papersnap.',
  path: '/terms',
})

const sections = [
  { id: 'account', n: '01', label: 'Your account' },
  { id: 'nature', n: '02', label: 'Nature of service' },
  { id: 'acceptable', n: '03', label: 'Acceptable use' },
  { id: 'risk', n: '04', label: 'Use at your own risk' },
  { id: 'warranties', n: '05', label: 'Disclaimer' },
  { id: 'liability', n: '06', label: 'Limitation of liability' },
  { id: 'ip', n: '07', label: 'Intellectual property' },
  { id: 'billing', n: '08', label: 'Subscriptions' },
  { id: 'privacy', n: '09', label: 'Data and privacy' },
  { id: 'modifications', n: '10', label: 'Modifications' },
  { id: 'governing', n: '11', label: 'Governing law' },
  { id: 'contact', n: '12', label: 'Contact us' },
]

const activeId = ref('account')
let observer = null

onMounted(() => {
  const els = sections.map((s) => document.getElementById(s.id)).filter(Boolean)
  observer = new IntersectionObserver(
    (entries) => entries.forEach((e) => { if (e.isIntersecting) activeId.value = e.target.id }),
    { rootMargin: '-90px 0px -60% 0px', threshold: 0 },
  )
  els.forEach((el) => observer.observe(el))
})
onBeforeUnmount(() => observer?.disconnect())

function go(id) {
  const el = document.getElementById(id)
  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 84, behavior: 'smooth' })
}
</script>

<template>
  <section class="legal-page py-12">
    <div class="priv">
      <div class="priv-head">
        <span class="priv-eyebrow">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg>
          Legal
        </span>
        <h1>Terms of Use</h1>
        <div class="priv-sub">
          <span class="updated-pill">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            Last updated June 15, 2026
          </span>
          <span class="who">Operated by <b>Keelara LLC</b></span>
        </div>
      </div>

      <div class="lead">
        <span class="lic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/><path d="m9 14 2 2 4-4"/></svg></span>
        <p>By accessing and using Papersnap (<a href="https://www.papersnap.io">www.papersnap.io</a>), operated by Keelara LLC ("we," "us," or "our"), <b>you agree to be bound by these Terms of Use</b>. If you do not agree, do not use the service.</p>
      </div>

      <div class="priv-grid">
        <aside class="toc">
          <p class="toc-label">On this page</p>
          <nav>
            <a v-for="s in sections" :key="s.id" :href="`#${s.id}`" :class="{ active: activeId === s.id }" @click.prevent="go(s.id)"><span class="tn">{{ s.n }}</span>{{ s.label }}</a>
          </nav>
        </aside>

        <div class="priv-content">
          <section class="sec" id="account">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span>
              <h2>Your account</h2>
            </div>
            <p>Papersnap requires an account. You are responsible for keeping your login credentials secure and for <b>all activity that occurs under your account</b>. Notify us promptly of any unauthorized use.</p>
          </section>

          <section class="sec" id="nature">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/><circle cx="12" cy="12" r="3"/></svg></span>
              <h2>Nature of the service</h2>
            </div>
            <p>Papersnap is an AI-powered tool that extracts structured data from documents you upload. Extraction results are generated by automated systems and third-party AI models.</p>
            <div class="keypoint warn">
              <span class="kic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></span>
              <p>Results <b>may be inaccurate, incomplete, or out of date</b>. You are responsible for reviewing and verifying extracted data before relying on it, particularly for accounting, tax, financial, or legal purposes.</p>
            </div>
            <p style="margin-top: 12px;">Papersnap is a productivity tool and does not provide accounting, tax, financial, or legal advice.</p>
          </section>

          <section class="sec" id="acceptable">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg></span>
              <h2>Acceptable use</h2>
            </div>
            <p>You agree to use Papersnap only for lawful purposes.</p>
            <div class="rules">
              <div class="rule allow">
                <span class="ric"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span>
                <span class="rtxt">You may only upload documents that <b>you own or have the right to process</b>.</span>
              </div>
              <div class="rule deny">
                <span class="ric"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></span>
                <span class="rtxt">You must not upload content that is <b>unlawful, infringing</b>, or that you are not authorized to share.</span>
              </div>
              <div class="rule deny">
                <span class="ric"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></span>
                <span class="rtxt">You must not attempt to <b>disrupt, reverse-engineer, or abuse</b> the service or its infrastructure.</span>
              </div>
            </div>
          </section>

          <section class="sec" id="risk">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg></span>
              <h2>Use at your own risk</h2>
            </div>
            <p>You use Papersnap at your own risk. The service is provided on an "as is" and "as available" basis.</p>
            <div class="asis-row">
              <span class="asis"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>AS IS</span>
              <span class="asis"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>AS AVAILABLE</span>
            </div>
            <p>We make no guarantees regarding the accuracy, completeness, or availability of the service or of any extraction results.</p>
          </section>

          <section class="sec" id="warranties">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="9" y1="11" x2="15" y2="11"/></svg></span>
              <h2>Disclaimer of warranties</h2>
            </div>
            <p>To the fullest extent permitted by applicable law, Keelara LLC disclaims all warranties, express or implied, including but not limited to implied warranties of <b>merchantability, fitness for a particular purpose, and non-infringement</b>.</p>
          </section>

          <section class="sec" id="liability">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3 4 6v6c0 5 3.5 7.5 8 9 4.5-1.5 8-4 8-9V6z"/><path d="M9 12h6"/></svg></span>
              <h2>Limitation of liability</h2>
            </div>
            <p>In no event shall Keelara LLC, its officers, directors, employees, or agents be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of Papersnap, including but not limited to damages resulting from <b>inaccurate extraction results or loss of data</b>.</p>
          </section>

          <section class="sec" id="ip">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M14.83 14.83a4 4 0 1 1 0-5.66"/></svg></span>
              <h2>Intellectual property</h2>
            </div>
            <p>All content comprising Papersnap itself — including text, graphics, logos, and software — is the property of Keelara LLC and is protected by applicable intellectual property laws.</p>
            <div class="keypoint">
              <span class="kic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span>
              <p>You retain all rights to the documents you upload and the data extracted from them; <b>we claim no ownership over your content</b>.</p>
            </div>
          </section>

          <section class="sec" id="billing">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg></span>
              <h2>Subscriptions and billing</h2>
            </div>
            <p>Paid plans (Solo, Business, and Team) are billed through our payment provider, Polar, on a recurring basis (monthly or annually) until cancelled. By subscribing, you authorize recurring charges. Cancellations and refunds are governed by our <RouterLink class="link" to="/refund">Refund Policy</RouterLink>.</p>
          </section>

          <section class="sec" id="privacy">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>
              <h2>Data and privacy</h2>
            </div>
            <p>Your use of Papersnap is also governed by our <RouterLink class="link" to="/privacy">Privacy Policy</RouterLink>, which explains how your data and uploaded documents are handled.</p>
          </section>

          <section class="sec" id="modifications">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></svg></span>
              <h2>Modifications to these terms</h2>
            </div>
            <p>We reserve the right to modify these Terms of Use at any time. Changes will be posted on this page with an updated revision date. <b>Continued use of the service after changes are posted constitutes acceptance of the updated terms.</b></p>
          </section>

          <section class="sec" id="governing">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18M5 7l7-4 7 4M5 7l-2 6a4 4 0 0 0 8 0L9 7M19 7l-2 6a4 4 0 0 0 8 0l-2-6M3 21h18"/></svg></span>
              <h2>Governing law</h2>
            </div>
            <p>These Terms of Use shall be governed by and construed in accordance with the laws of the <b>United States</b>, without regard to conflict of law principles.</p>
          </section>

          <section class="sec contact" id="contact">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg></span>
              <h2>Contact us</h2>
            </div>
            <p>If you have questions about these Terms of Use, please contact us at:</p>
            <div class="contact-row">
              <a class="mail-btn" href="mailto:contact@keelara.com">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/><polyline points="22 6 12 13 2 6"/></svg>
                contact@keelara.com
              </a>
              <span class="contact-note">Papersnap is operated by <b>Keelara LLC</b>.</span>
            </div>
          </section>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.legal-page {
  --fg-1: #0f172a; --fg-2: #334155; --fg-3: #475569; --fg-4: #64748b;
  --panel: #ffffff; --sunk: #f8fafc;
  --bd-1: #e2e8f0; --bd-2: #cbd5e1;
  --accent: rgb(var(--color-primary));
  --accent-soft: rgb(var(--color-primary) / 0.10);
  --em-bg: rgb(16 185 129 / 0.10); --em-bd: rgb(16 185 129 / 0.30); --em-ic-bg: rgb(16 185 129 / 0.16); --em-ic: #059669; --em-fg: #047857;
  --amber-ic: #d97706; --amber-fg: #b45309;
  --red-ic: #dc2626;
}
.dark .legal-page {
  --fg-1: #f8fafc; --fg-2: #cbd5e1; --fg-3: #94a3b8; --fg-4: #64748b;
  --panel: #0f172a; --sunk: rgb(2 6 23 / 0.5);
  --bd-1: #1e293b; --bd-2: #334155;
  --accent: rgb(45 212 191); --accent-soft: rgb(45 212 191 / 0.10);
  --em-ic: #34d399; --em-fg: #6ee7b7;
  --amber-ic: #fcd34d; --amber-fg: #fcd34d;
  --red-ic: #f87171;
}

.priv { max-width: 980px; margin: 0 auto; width: 100%; }
.priv-head { margin: 4px 0 26px; }
.priv-eyebrow { display: inline-flex; align-items: center; gap: 7px; font-family: var(--font-mono); font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--accent); margin-bottom: 12px; }
.priv-eyebrow svg { width: 13px; height: 13px; }
.priv-head h1 { font-family: var(--font-display); font-weight: 700; font-size: 32px; color: var(--fg-1); margin: 0; letter-spacing: -0.01em; }
.priv-sub { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 12px; }
.updated-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 11px; border-radius: 9999px; background: var(--sunk); border: 1px solid var(--bd-2); font-size: 12px; font-weight: 600; color: var(--fg-2); }
.updated-pill svg { width: 12px; height: 12px; color: var(--fg-4); }
.priv-sub .who { font-size: 13px; color: var(--fg-4); }
.priv-sub .who b { color: var(--fg-3); font-weight: 600; }

.lead { display: flex; gap: 14px; align-items: flex-start; padding: 18px 20px; border-radius: 14px; margin-bottom: 28px; background: rgb(var(--color-primary) / 0.07); border: 1px solid rgb(var(--color-primary) / 0.22); }
.lead .lic { width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0; background: rgb(var(--color-primary) / 0.16); color: var(--accent); display: inline-flex; align-items: center; justify-content: center; }
.lead .lic svg { width: 18px; height: 18px; }
.lead p { margin: 0; font-size: 14px; line-height: 1.65; color: var(--fg-2); }
.lead p a { color: var(--accent); font-weight: 600; }
.lead p b { color: var(--fg-1); font-weight: 600; }

.priv-grid { display: grid; grid-template-columns: 210px 1fr; gap: 34px; align-items: start; }
@media (max-width: 860px) { .priv-grid { grid-template-columns: 1fr; gap: 18px; } }
.toc { position: sticky; top: 84px; }
@media (max-width: 860px) { .toc { display: none; } }
.toc-label { font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--fg-4); margin: 0 0 10px 12px; }
.toc nav { display: flex; flex-direction: column; gap: 1px; }
.toc a { display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500; color: var(--fg-3); border-left: 2px solid transparent; transition: all .15s ease; }
.toc a:hover { color: var(--fg-1); background: var(--sunk); }
.toc a.active { color: var(--accent); background: var(--accent-soft); border-left-color: rgb(var(--color-primary)); }
.toc a .tn { font-family: var(--font-mono); font-size: 11px; color: var(--fg-4); width: 16px; flex-shrink: 0; }
.toc a.active .tn { color: var(--accent); }

.priv-content { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.sec { background: var(--panel); border: 1px solid var(--bd-1); border-radius: 15px; padding: 24px 26px; scroll-margin-top: 90px; }
.sec-head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.sec-ic { width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0; background: rgb(var(--color-primary) / 0.14); color: var(--accent); display: inline-flex; align-items: center; justify-content: center; }
.sec-ic svg { width: 18px; height: 18px; }
.sec-head h2 { font-family: var(--font-display); font-weight: 600; font-size: 18px; color: var(--fg-1); margin: 0; }
.sec p { font-size: 14px; line-height: 1.65; color: var(--fg-3); margin: 0 0 12px; }
.sec p:last-child { margin-bottom: 0; }
.sec p b { color: var(--fg-1); font-weight: 600; }
.sec .link { color: var(--accent); font-weight: 600; }

.keypoint { display: flex; gap: 12px; align-items: flex-start; margin-top: 14px; padding: 13px 16px; border-radius: 11px; background: var(--em-bg); border: 1px solid var(--em-bd); }
.keypoint .kic { width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0; background: var(--em-ic-bg); color: var(--em-ic); display: inline-flex; align-items: center; justify-content: center; }
.keypoint .kic svg { width: 15px; height: 15px; }
.keypoint p { font-size: 13.5px; line-height: 1.55; color: var(--fg-2); margin: 0; align-self: center; }
.keypoint p b { color: var(--em-fg); font-weight: 700; }
.keypoint.warn { background: rgb(245 158 11 / 0.08); border-color: rgb(245 158 11 / 0.3); }
.keypoint.warn .kic { background: rgb(245 158 11 / 0.16); color: var(--amber-ic); }
.keypoint.warn p b { color: var(--amber-fg); }

.rules { display: flex; flex-direction: column; gap: 8px; margin-top: 4px; }
.rule { display: flex; align-items: flex-start; gap: 11px; padding: 11px 14px; border-radius: 11px; background: var(--sunk); border: 1px solid var(--bd-1); }
.rule .ric { width: 24px; height: 24px; border-radius: 7px; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; margin-top: 1px; }
.rule .ric svg { width: 14px; height: 14px; }
.rule.allow .ric { background: rgb(16 185 129 / 0.15); color: var(--em-ic); }
.rule.deny .ric { background: rgb(239 68 68 / 0.14); color: var(--red-ic); }
.rule .rtxt { font-size: 13.5px; line-height: 1.55; color: var(--fg-2); }
.rule .rtxt b { color: var(--fg-1); font-weight: 600; }

.asis-row { display: flex; gap: 10px; flex-wrap: wrap; margin: 2px 0 12px; }
.asis { display: inline-flex; align-items: center; gap: 7px; padding: 8px 14px; border-radius: 10px; font-family: var(--font-mono); font-size: 12.5px; font-weight: 700; letter-spacing: 0.03em; background: var(--sunk); border: 1px solid var(--bd-2); color: var(--fg-1); }
.asis svg { width: 14px; height: 14px; color: var(--fg-4); }

.sec.contact { background: linear-gradient(180deg, rgb(var(--color-primary) / 0.06), var(--panel)); border-color: rgb(var(--color-primary) / 0.22); }
.contact-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.mail-btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 16px; border-radius: 10px; background: rgb(var(--color-primary)); color: #fff; font-weight: 600; font-size: 13.5px; transition: background .15s ease; }
.mail-btn:hover { background: rgb(var(--color-primary) / 0.88); }
.mail-btn svg { width: 15px; height: 15px; }
.contact-note { font-size: 13px; color: var(--fg-4); }
.contact-note b { color: var(--fg-3); font-weight: 600; }
</style>
